import { FormEvent, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import {
  createProfileFromInvitation,
  loadAdminInvitation,
} from "../services/adminAccountService";
import { writeAuditEvent } from "../services/auditService";
import type { AdminInvitation } from "../types/auth";
import { normalizeEmail } from "../utils/firestoreData";

type AcceptanceMode = "create" | "signin";

function getInvitationId(): string {
  const [, rawQuery = ""] = window.location.hash.split("?");
  const params = new URLSearchParams(rawQuery);
  return params.get("invite")?.trim() ?? "";
}

export function InvitationAcceptance() {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<AdminInvitation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<AcceptanceMode>("create");
  const [password, setPassword] = useState("");
  const [success, setSuccess] = useState(false);
  const invitationId = getInvitationId();

  useEffect(() => {
    if (!invitationId) {
      setError("Le lien d'invitation est incomplet.");
      return;
    }

    void loadAdminInvitation(invitationId)
      .then((nextInvitation) => {
        if (!nextInvitation) {
          setError("Cette invitation n'existe pas ou n'est plus accessible.");
          return;
        }

        setInvitation(nextInvitation);
        setEmail(nextInvitation.email);
        setDisplayName(nextInvitation.displayName);
      })
      .catch((loadError: unknown) => {
        console.error(loadError);
        setError("Impossible de charger cette invitation.");
      });
  }, [invitationId]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!auth || !invitation) {
      setError("Firebase n'est pas configuré.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const normalizedEmail = normalizeEmail(email);
      const credential =
        mode === "create"
          ? await createUserWithEmailAndPassword(auth, normalizedEmail, password)
          : await signInWithEmailAndPassword(auth, normalizedEmail, password);

      const profile = await createProfileFromInvitation({
        displayName,
        invitation,
        user: credential.user,
      });

      await writeAuditEvent({
        actorUid: credential.user.uid,
        eventType: "admin.invitation.accepted",
        metadata: {
          invitationId: invitation.id,
          role: profile.role,
        },
      });

      setSuccess(true);
      window.location.hash = "/";
    } catch (submitError) {
      console.error(submitError);
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Impossible d'activer ce compte administrateur.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="accept-title">
        <p className="eyebrow">Invitation administrateur</p>
        <h1 id="accept-title">Activer l'accès</h1>

        {invitation?.status === "accepted" ? (
          <div className="alert alert-info" role="status">
            Cette invitation a déjà été utilisée. Connectez-vous avec le compte
            associé.
          </div>
        ) : null}

        {error ? (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="alert alert-success" role="status">
            Compte activé. Redirection vers l'administration.
          </div>
        ) : null}

        <div className="segmented" role="tablist" aria-label="Mode d'activation">
          <button
            aria-selected={mode === "create"}
            className={mode === "create" ? "active" : ""}
            onClick={() => setMode("create")}
            type="button"
          >
            Nouveau compte
          </button>
          <button
            aria-selected={mode === "signin"}
            className={mode === "signin" ? "active" : ""}
            onClick={() => setMode("signin")}
            type="button"
          >
            Compte existant
          </button>
        </div>

        <form className="stack" onSubmit={handleSubmit}>
          <label className="field">
            <span>Email invité</span>
            <input
              autoComplete="email"
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              readOnly={Boolean(invitation)}
              required
              type="email"
              value={email}
            />
          </label>
          <label className="field">
            <span>Nom affiché</span>
            <input
              autoComplete="name"
              onChange={(event) => setDisplayName(event.target.value)}
              required
              type="text"
              value={displayName}
            />
          </label>
          <label className="field">
            <span>{mode === "create" ? "Créer un mot de passe" : "Mot de passe"}</span>
            <input
              autoComplete={mode === "create" ? "new-password" : "current-password"}
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          <button
            className="button button-primary"
            disabled={!invitation || invitation.status !== "pending" || isSubmitting}
            type="submit"
          >
            {isSubmitting ? "Activation..." : "Activer le compte"}
          </button>
        </form>
      </section>
    </main>
  );
}
